import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErrorState,
  errorStateActionClassName,
} from '../../components/smart/ui/ErrorState';

const GALLERY_SUPPORT = {
  email: 'support@example.com',
  whatsapp: '+15551234567',
} as const;

/**
 * Gallery maintenance screen — same chrome as smart ErrorPage / flow-smart
 * MaintenancePage (Tailwind tokens), not the shared lola AuraLayout.
 */
export const MaintenancePage = () => {
  const { t } = useTranslation('smart');
  const [supportOpen, setSupportOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const goToWhatsApp = useCallback(() => {
    window.open(
      `https://wa.me/${GALLERY_SUPPORT.whatsapp.replace(/\D/g, '')}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  const sendEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(GALLERY_SUPPORT.email);
      setCopied(true);
    } catch {
      // Clipboard may be unavailable; still open the mail client.
    }
    window.open(
      `mailto:${GALLERY_SUPPORT.email}`,
      '_blank',
      'noopener,noreferrer',
    );
  }, []);

  return (
    <div className="smart-root">
      <ErrorState
        title={t('error.maintenanceTitle')}
        message={t('error.maintenanceMessage')}
        action={
          <button
            type="button"
            className={errorStateActionClassName}
            onClick={() => setSupportOpen(true)}
          >
            {t('error.contactSupport')}
          </button>
        }
      />

      {supportOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40"
          onClick={() => setSupportOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('contact.chooseOption')}
            className="bg-bg w-full max-w-md rounded-t-2xl px-6 pt-5 pb-8"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">
                {t('contact.chooseOption')}
              </h2>
              <button
                type="button"
                className="text-fg-muted text-sm font-semibold"
                aria-label={t('common.cancel')}
                onClick={() => setSupportOpen(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="bg-accent text-accent-fg rounded-xl px-6 py-3 text-sm font-semibold"
                onClick={() => void sendEmail()}
              >
                {t('contact.email')}
              </button>
              <button
                type="button"
                className="bg-accent text-accent-fg rounded-xl px-6 py-3 text-sm font-semibold"
                onClick={goToWhatsApp}
              >
                {t('contact.whatsapp')}
              </button>
              {copied ? (
                <p className="text-fg-muted text-xs">{t('contact.emailCopied')}</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
